import { QueryClient } from '@tanstack/react-query';
// Use a lightweight local QueryFunction type to avoid transform issues
type QueryFunctionLocal<T = unknown> = (context: { queryKey: readonly unknown[] }) => T | Promise<T>;

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    // Try to parse JSON error bodies to extract a friendly message
    let friendly = text;
    try {
      const parsed = text ? JSON.parse(text) : null;
      if (parsed) {
        if (typeof parsed.message === 'string' && parsed.message.trim()) {
          friendly = parsed.message;
        } else if (typeof parsed.error === 'string' && parsed.error.trim()) {
          friendly = parsed.error;
        } else if (parsed.errors) {
          // common shape: { errors: [...] }
          try {
            friendly = Array.isArray(parsed.errors) ? parsed.errors.map((e: any) => e.message || e).join(', ') : String(parsed.errors);
          } catch (e) {
            friendly = String(parsed.errors);
          }
        }
      }
    } catch (e) {
      // not JSON, keep original text
    }

    throw new Error(`${res.status}: ${friendly}`);
  }
}

export async function apiRequest(method: string, url: string, data?: unknown) {
  // If a /v1/... path is passed in the client, map it to /api/... so Vite dev proxy handles it
  let requestUrl = url;
  if (typeof url === 'string' && url.startsWith('/v1/')) {
    requestUrl = `/api${url.slice(3)}`;
  }

  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('admin_token') : null;
  const headers: Record<string, string> = data ? { 'Content-Type': 'application/json' } : {};
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(requestUrl, {
    method,
    headers,
    body: data ? JSON.stringify(data) : undefined,
    credentials: 'include',
  });

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = 'returnNull' | 'throw';

export function getQueryFn<T>(options: { on401: UnauthorizedBehavior }): QueryFunctionLocal<T> {
  const { on401: unauthorizedBehavior } = options;

  return async ({ queryKey }) => {
    const url = queryKey.join('/') as string;
    // Resolve users API to backend host so DevTools shows backend URL instead of frontend proxy
    const BACKEND = (import.meta as any).env.VITE_BACKEND_URL || (import.meta as any).env.VITE_API_URL || null;
    let requestUrl = url;
    if (typeof url === 'string' && url.startsWith('/api/users')) {
      const suffix = url.replace(/^\/api/, ''); // /users or /users/123
      if (BACKEND) requestUrl = `${BACKEND}/v1${suffix}`.replace(/\/\//g, '/').replace('http:/', 'http://');
      else requestUrl = `http://localhost:3041/v1${suffix}`;
    }

    try {
      const res = await fetch(requestUrl, {
        credentials: 'include',
      });

      if (res.status === 401) {
        if (unauthorizedBehavior === 'returnNull') return null as unknown as T;
      }

      await throwIfResNotOk(res);
      return (await res.json()) as T;
    } catch (err) {
      // Provide dev mocks when backend/proxy is unreachable
      if (import.meta.env.DEV) {
        if (url.includes('/api/analytics/dashboard')) {
          console.info('[queryClient] returning mock dashboard data (fetch failed) for', url);
          const mock: any = {
            totalContacts: 124,
            totalDeals: 42,
            totalValue: 125000,
            wonDeals: 12,
            dealsByStage: [
              { stage: 'lead', count: 10, value: 5000 },
              { stage: 'qualified', count: 8, value: 15000 },
              { stage: 'proposal', count: 6, value: 25000 },
              { stage: 'negotiation', count: 12, value: 40000 },
              { stage: 'won', count: 6, value: 35000 },
            ],
            recentActivities: 23,
          };
          return mock as T;
        }
        if (url.includes('/api/contacts')) {
          console.info('[queryClient] returning mock contacts (fetch failed) for', url);
          const mockContacts: any = [
            {
              id: 'c_1',
              firstName: 'Alice',
              lastName: 'Johnson',
              email: 'alice@example.com',
              phone: '+1 555-0123',
              company: 'Starlight Co',
              position: 'CEO',
              status: 'active',
              notes: 'Important client',
            },
            {
              id: 'c_2',
              firstName: 'Bob',
              lastName: 'Smith',
              email: 'bob@example.com',
              phone: '+1 555-0456',
              company: 'Acme LLC',
              position: 'CTO',
              status: 'lead',
              notes: '',
            },
            {
              id: 'c_3',
              firstName: 'Carol',
              lastName: 'Nguyen',
              email: 'carol@example.com',
              phone: '+1 555-0789',
              company: '',
              position: '',
              status: 'inactive',
              notes: 'Follow up next quarter',
            },
          ];
          return mockContacts as unknown as T;
        }
        if (url.includes('/api/users')) {
          console.info('[queryClient] returning mock users (fetch failed) for', url);
          const mockUsers: any = [
            { id: 'u_1', email: 'owner@crm.com', name: 'Owner User', role: 'owner', isActive: true },
            { id: 'u_2', email: 'admin@crm.com', name: 'Admin User', role: 'admin', isActive: true },
            { id: 'u_3', email: 'jane.doe@example.com', name: 'Jane Doe', role: 'intern', isActive: false },
          ];
          return mockUsers as unknown as T;
        }
      }
      throw err;
    }
  };
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: 'throw' }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});

export default queryClient;
